import React, { useState } from 'react'
import SelectInput2 from '@toasttab/shared/src/components/atoms/Forms/SelectInput/SelectInput2'
import FormField from '@toasttab/shared/src/components/atoms/Forms/FormField/FormField'
import TextInput from '@toasttab/shared/src/components/atoms/Forms/TextInput/TextInput'
import {
  CheckboxInput,
  Button,
  RadioInput,
  RadioInputTile,
  TextArea,
  DateInput2,
  FileInput
} from '@toasttab/shared/src/components'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import classNames from 'classnames'
import { FormStepStatementOptions } from '@toasttab/shared/src/utils/Contentful/richText'
import {
  usePageContext,
  PageContextProps
} from '@toasttab/shared/src/hooks/usePageContext'
import { captureMessage, setTag, addBreadcrumb } from '@sentry/gatsby'
export interface Field extends React.InputHTMLAttributes<HTMLInputElement> {
  fieldDependentId?: string
  fieldDependency?: Field[] | null | []
  fieldInputNameMarketoRestApi: string
  fieldLabel: string
  fieldOptions: { key: string; value: string }[] | []
  fieldType: string
  isRequired: boolean
  marketoFieldName: string
  title: string
  interaction?: string
  __typename: string
}

export type CoreFieldsProps = Field | Statement

export interface Statement {
  title: string
  statement: any
  __typename: string
}

export interface FormStepProps {
  __typename?: string
  color?: 'default' | 'inverse'
  coreFields: Field[] | Statement[]
  description: any
  fieldIdPrefix?: string
  formik: any
  handleSubmit?: any
  heading?: string
  headingAlignCenter?: boolean
  headingList?: any
  heapInteractionSubmit?: string
  isActive?: boolean
  isLastStep?: boolean
  isMultiStepsForm?: boolean
  isProgressiveEligibleForm?: boolean
  isProgressiveForm?: boolean
  isSubmitting?: boolean
  layout?: 'default' | 'inline' | 'twoColumn'
  numSteps?: number
  progressiveFields: Field[]
  submitLabel: string
  title: string
}

const FormStep = ({
  color = 'default',
  coreFields,
  description,
  fieldIdPrefix,
  formik,
  handleSubmit,
  heading,
  headingAlignCenter,
  headingList,
  heapInteractionSubmit = '',
  isActive,
  isLastStep,
  isProgressiveEligibleForm,
  isProgressiveForm,
  isSubmitting,
  layout = 'default',
  numSteps = 0,
  progressiveFields,
  submitLabel,
  title
}: FormStepProps) => {
  const pageData: PageContextProps = usePageContext() || null
  const pageLP = pageData?.isLP ?? false
  const [validating, setValidating] = useState(false)
  const fieldsetClasses = classNames('', {
    block: layout === 'default' && isActive,
    'flex flex-col md:flex-row gap-4 w-full md:items-center':
      layout === 'inline' && isActive,
    hidden: !isActive
  })
  const render = () => {
    if (isProgressiveEligibleForm) {
      if (isProgressiveForm) {
        return renderFields(progressiveFields)
      } else {
        return renderFields(coreFields)
      }
    } else {
      return renderFields(coreFields)
    }
  }
  const fillOptions = (
    fieldLabel: string,
    options: { key: string; value: string }[],
    isRequired: boolean
  ) => {
    const optionArr: { label: string; value: string; disabled: boolean }[] = [
      { label: fieldLabel + (isRequired ? '*' : ''), value: '', disabled: true }
    ]
    options.map((o: { key: string; value: string }) => {
      return optionArr.push({
        label: o.key,
        value: o.value,
        disabled: false
      })
    })
    return optionArr
  }
  const renderFields = (fields: Field[] | Statement[]): React.JSX.Element[] => {
    return fields?.map((field: any, i: number) => {
      if (field.__typename === 'ContentfulMdsFormField') {
        const f = field as Field

        let changeFunction = formik.handleChange
        if (formik.values['formid'] === '1008') {
          const subscriptionPreferencesFields = [
            'blogSubscriber',
            'dataSubscriber',
            'eventsSubscriber',
            'enterpriseNewsletterSubscriber'
          ]

          changeFunction = (e: any) => {
            formik.setFieldValue(e.target.name, e.target.checked, false)
            if (e.target.name === 'unsubscribed') {
              subscriptionPreferencesFields.map((field) => {
                formik.setFieldValue(field, false, false)
              })
            } else {
              formik.setFieldValue('unsubscribed', false, false)
            }
          }
        }

        return (
          <React.Fragment key={i}>
            <FormField
              classes={f.fieldInputNameMarketoRestApi}
              fullWidth
              hidden={f.fieldType === 'Hidden'}
            >
              {f.fieldType === 'Text' && (
                <TextInput
                  fullWidth
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  padding='large'
                  placeholder={f.fieldLabel}
                  required={f.isRequired}
                  type='text'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'Email' && (
                <TextInput
                  fullWidth
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  padding='large'
                  placeholder={f.fieldLabel}
                  required={f.isRequired}
                  type='email'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'Phone' && (
                <TextInput
                  fullWidth
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  padding='large'
                  placeholder={f.fieldLabel}
                  required={f.isRequired}
                  type='tel'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'Date' && (
                <DateInput2
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  max='2100-12-31'
                  min='1900-01-01'
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder={f.fieldLabel}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'Number' && (
                <TextInput
                  fullWidth
                  interaction={f.interaction ?? ''}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  max={f.max ?? null}
                  min={f.min ?? null}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  padding='large'
                  placeholder={f.fieldLabel}
                  type='number'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'Hidden' && (
                <TextInput
                  fullWidth
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  padding='large'
                  placeholder={f.fieldLabel}
                  type='hidden'
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'File' && (
                <FileInput
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder={f.fieldLabel}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
              {f.fieldType === 'Select (Dropdown)' && (
                <SelectInput2
                  fullWidth={true}
                  interaction={f.interaction ?? ''}
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  padding='large'
                  required={f.isRequired}
                  variation={color}
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  options={fillOptions(
                    f.fieldLabel,
                    f.fieldOptions,
                    f.isRequired
                  )}
                />
              )}
              {f.fieldType === 'Checkbox (Single/Multi Select)' && (
                <CheckboxInput
                  labels={null}
                  name={f.fieldInputNameMarketoRestApi}
                  onChange={changeFunction}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                  checked={
                    formik.values[f.fieldInputNameMarketoRestApi] ?? false
                  }
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  label={
                    f.fieldOptions
                      ? f.fieldOptions[0].key + (f.isRequired ? '*' : '')
                      : null
                  }
                />
              )}
              {f.fieldType === 'Radio (Single-select)' && (
                <RadioInput
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onChange={formik.handleChange}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  options={f.fieldOptions.map(
                    (o: { key: string; value: string }) => ({
                      label: o.key,
                      value: o.value
                    })
                  )}
                />
              )}
              {f.fieldType === 'Radio (Single-select Tile)' && (
                <RadioInputTile
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onChange={formik.handleChange}
                  required={f.isRequired}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? null}
                  variation='default'
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                  options={f.fieldOptions.map(
                    (o: { key: string; value: string }) => ({
                      label: o.key,
                      value: o.value
                    })
                  )}
                />
              )}
              {f.fieldType === 'Text Area' && (
                <TextArea
                  label={f.fieldLabel + (f.isRequired ? '*' : '')}
                  name={f.fieldInputNameMarketoRestApi}
                  onChange={formik.handleChange}
                  value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
                  variation='default'
                  error={
                    (validating ||
                      formik.touched[f.fieldInputNameMarketoRestApi]) &&
                    formik.errors[f.fieldInputNameMarketoRestApi]
                      ? (formik.errors[
                          f.fieldInputNameMarketoRestApi
                        ] as string[])
                      : ''
                  }
                  id={`${fieldIdPrefix}-${
                    f.marketoFieldName || f.fieldInputNameMarketoRestApi
                  }`}
                />
              )}
            </FormField>
            {f.fieldDependency?.map((field: any) => {
              const parentFieldValue =
                formik.values[f.fieldInputNameMarketoRestApi] ?? ''
              const dependentFieldValue = field.formFieldValue
              if (
                field.nextFormFieldFormStep &&
                parentFieldValue === dependentFieldValue
              ) {
                return renderFields([field.nextFormFieldFormStep])
              }
              return null
            })}
          </React.Fragment>
        )
      } else if (field.__typename === 'ContentfulMdsStatement') {
        // const f = field as Statement
        return (
          <React.Fragment key={i}>
            <span className=''>
              {renderRichText(field.statement, FormStepStatementOptions)}
            </span>
          </React.Fragment>
        )
      } else {
        return <React.Fragment key={i} />
      }
    })
  }
  const handleNextStep = async () => {
    setValidating(true)
    const errors = await formik.validateForm(formik.values)
    if (Object.keys(errors).length === 0) {
      const formStep = isLastStep ? formik.values.step : formik.values.step + 1
      formik.setValues({ ...formik.values, step: formStep })
    } else {
      formik.setErrors(errors)
      if (isLastStep) {
        const sentryInvalidFields = Object.keys(errors).map((key) => {
          console.log(key, formik.values[key as keyof typeof formik.values])
          addBreadcrumb({
            category: 'invalid field',
            message: `${key}: ${
              formik.values[key as keyof typeof formik.values] ?? null
            }`,
            level: 'info'
          })
          return {
            [key]: formik.values[key as keyof typeof formik.values] ?? null
          }
        })

        setTag('form_valid', false)
        setTag('invalid_fields', JSON.stringify(sentryInvalidFields))
        captureMessage(
          `Form Validation Failed: ${JSON.stringify(sentryInvalidFields)}`,
          'info'
        )
      }
    }
    if (isLastStep && validating) {
      if (handleSubmit) {
        handleSubmit()
      } else {
        formik.handleSubmit()
      }
    }
  }

  const formFieldWrapperClasses = classNames(
    'md:grid md:grid-cols-2 md:gap-x-4',
    {
      'md:[&>div:last-child]:col-span-2':
        (!isProgressiveForm && coreFields.length % 2 !== 0) ||
        (isProgressiveForm && progressiveFields?.length % 2 !== 0)
    }
  )

  const headingClasses = classNames({
    'mb-4': pageData?.styleContext !== 'request-demo',
    'mb-1.5': pageData?.styleContext === 'request-demo',
    'text-center':
      ['fullWidth', 'twoColumn'].includes(layout) || headingAlignCenter
  })

  const headingListIcon = (icon: string) => {
    switch (icon) {
      case 'bullet-gray':
        return 'before:bg-icon-gray-bullet'
      case 'checkmark-gray':
        return 'before:bg-icon-gray-check'
      case 'checkmark-circle-solid-gray':
        return 'before:bg-icon-gray-checkmark-circle'
      case 'checkmark-orange':
        return 'before:bg-icon-orange-check'
      case 'checkmark-circle-solid-orange':
        return 'before:bg-icon-orange-circle-solid-checkmark'
      case 'plus-circle-gray':
        return 'before:bg-icon-gray-plus-circle'
      case 'checkmark-green':
        return 'before:bg-icon-checkmark-green'
      default:
        return ''
    }
  }

  const headingVariation =
    numSteps > 1 || pageLP || ['fullWidth', 'twoColumn'].includes(layout)
      ? 'h3'
      : 'h2'

  return (
    <div className={fieldsetClasses}>
      {numSteps > 1 && !pageLP && (
        <Typography
          allowHtml={false}
          as='p'
          classes='uppercase text-secondary'
          variation='overline'
        >
          STEP {formik.values.step + 1} / {numSteps}
        </Typography>
      )}
      {heading ? (
        <Typography as='h3' classes={headingClasses} variation='h3'>
          {heading}
        </Typography>
      ) : null}
      {headingList && headingList.length > 0 ? (
        <ul className='list-none mb-3.5'>
          {headingList.map((item: any, i: number) => (
            <li
              className='flex text-sm text-secondary mb-1 last:mb-none'
              key={i}
            >
              <span
                className={`mr-2 h-4 w-4 before:mr-1 before:h-4 before:w-4 before:inline-block ${headingListIcon(
                  item.key
                )}`}
              />
              {item.value.content[0].content[0].value}
            </li>
          ))}
        </ul>
      ) : null}
      {title && !heading ? (
        <Typography
          as={headingVariation}
          classes={headingClasses}
          variation={headingVariation}
        >
          {title}
        </Typography>
      ) : null}
      {description?.description ? (
        <p className='text-gray-100 mb-8'>{description?.description}</p>
      ) : null}
      {layout === 'twoColumn' ? (
        <div className={formFieldWrapperClasses}>{render()}</div>
      ) : (
        render()
      )}
      <Button
        arrow={false}
        classes='mt-1 whitespace-pre'
        disabled={isSubmitting}
        fullwidth
        heapInteraction={isLastStep ? heapInteractionSubmit : ''}
        onClick={handleNextStep}
        size='medium'
        type={isLastStep ? 'submit' : 'button'}
        variation={color === 'inverse' ? 'secondary' : 'primary'}
      >
        {isSubmitting ? (
          <div className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] '>
            <img
              alt='loadingspinner lazyload'
              className='w-[43px] h-[43px] grayscale'
              loading='lazy'
              src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
            />
          </div>
        ) : null}
        <div
          className={`${
            isSubmitting ? 'invisible' : 'font-semibold'
          } leading-none`}
        >
          {submitLabel}
        </div>
      </Button>
    </div>
  )
}

export default FormStep
